<template>
    <div class="component component__system_uploader_pdf">
        <component-text
            :class="'component__system_uploader_pdf_description'"
        >Attachments are documents that learners can download when they are enrolled on to the course.
        </component-text>
        <component-button
            :disabled="disabled"
            :class="'component__system_uploader_pdf_button'"
            :name="'component-system-uploader-pdf-button'"
            :theme="'default'"
            :icon="true"
            :icon-align="'right'"
            :icon-name="'upload'"
            :align="'left'"
            :hint="'Format PDF only'"
            @click="openFileUploader"
        >Upload
        </component-button>
        <template v-if="existingFiles.length">
            <component-wrapper>
                <div class="columns is-variable">
                    <div class="column is-narrow">
                        <component-title
                            :raw="true"
                            :size="'small'"
                        >Uploaded files
                        </component-title>
                    </div>
                    <div class="column">
                        <div class="component__system_uploader_pdf_totalsize">
                            {{ `${(filesTotalSize / (1024*1024)).toFixed(2)}MB of ${(maximumFileSize / (1024*1024)).toFixed(0)}MB` }}
                        </div>
                    </div>
                </div>
                <div class="component__system_uploader_pdf_files">
                    <template v-for="(file, index) in existingFiles">
                        <div
                            :key="index"
                            class="component__system_uploader_pdf_files_item"
                        >
                            <div class="columns is-vcentered">
                                <div class="column">
                                    <div class="columns is-vcentered is-variable is-1">
                                        <div class="column">
                                            <div class="component__system_uploader_pdf_files_item_name">
                                                {{ file.name }}
                                            </div>
                                        </div>
                                        <div class="column is-narrow">
                                            <div class="component__system_uploader_pdf_files_item_size">
                                                {{ `(${file.loaded && file.loaded !== file.total ? (file.loaded / (1024*1024)).toFixed(2) + 'MB / ' : ''}${((file.total ? file.total : file.size) / (1024*1024)).toFixed(2) + 'MB)'}` }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <template v-if="file.url">
                                    <div class="column is-narrow">
                                        <component-button
                                            :disabled="file.loaded !== file.total || disabled"
                                            :class="'component__system_uploader_pdf_files_item_preview'"
                                            :name="'component-system-uploader-pdf-files-item-preview'"
                                            :to-external="file.url"
                                            :target="true"
                                            :theme="'default'"
                                        >Preview
                                        </component-button>
                                    </div>
                                </template>
                                <template v-if="!file.locked">
                                    <div class="column is-narrow">
                                        <component-button
                                            :disabled="file.loaded !== file.total || disabled"
                                            :class="'component__system_uploader_pdf_files_item_remove'"
                                            :name="'component-system-uploader-pdf-files-item-remove'"
                                            :theme="'default'"
                                            :icon="true"
                                            :icon-name="'trash-2'"
                                            @click="removeFile(file.url)"
                                        />
                                    </div>
                                </template>
                                <template v-if="file.locked">
                                    <div class="column is-narrow">
                                        <form-checkbox
                                            :disabled="disabled"
                                            :label="'Hide attachment'"
                                            :class="'component__system_uploader_pdf_files_item_hide'"
                                            :name="`component-system-uploader-pdf-files-item-hide-${index}`"
                                            :value="file.hide"
                                            @input="setFileProp({ url: file.url, key: 'hide', value: !file.hide })"
                                        ></form-checkbox>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </template>
                </div>
            </component-wrapper>
        </template>
        <input
            ref="uploader"
            class="component__system_uploader_pdf_input is-hidden"
            type="file"
            accept=".pdf"
            multiple
            :name="'component-system-uploader-pdf-input'"
            :id="`component-system-uploader-pdf-input`"
            @change="setFiles"
        >
    </div>
</template>

<script>
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentText from '@/components/default/shared/ComponentText'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'

export default {
  name: 'component-system-uploader-pdf',
  components: { ComponentWrapper, FormCheckbox, ComponentTitle, ComponentText, ComponentButton },
  data () {
    return {
      files: [],
      rerenderIndex: 0
    }
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    isCourse: {
      type: Boolean,
      default: true
    },
    isCatalogItem: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    getFilesGetterNameCourse () {
      return 'getEbsCourseFiles'
    },
    getFilesGetterNameCatalogItem () {
      return 'getEbsCatalogItemFiles'
    },
    setFileDispatchName () {
      return this.isCatalogItem ? 'ebsCatalogItemSetFile' : 'ebsCourseSetFile'
    },
    setFilePropDispatchName () {
      return 'ebsCourseSetFileProp'
    },
    uploadFileDispatchName () {
      return this.isCatalogItem ? 'ebsCatalogItemUploadFile' : 'ebsCourseUploadFile'
    },
    removeFileDispatchName () {
      return this.isCatalogItem ? 'ebsCatalogItemRemoveFile' : 'ebsCourseRemoveFile'
    },
    generatePresignedUrlDispatchName () {
      return this.isCatalogItem ? 'ebsCatalogItemGeneratePresignedPost' : 'ebsCourseGeneratePresignedPost'
    },
    existingFiles () {
      const courseFiles = this.$store.getters[this.getFilesGetterNameCourse]
      const courseFilesMapped = courseFiles.length ? courseFiles.map(obj => {
        return {
          name: obj.name,
          size: obj.size,
          url: obj.url,
          locked: this.isCatalogItem,
          hide: obj.hide,
          total: obj.total,
          loaded: obj.loaded
        }
      }) : []
      const catalogItemFiles = this.$store.getters[this.getFilesGetterNameCatalogItem]

      return this.isCatalogItem ? [...courseFilesMapped, ...catalogItemFiles] : courseFilesMapped
    },
    preHiddenFiles () {
      return this.$store.getters.getEbsCatalogItemHiddenCourseFiles
    },
    filesTotalSize () {
      const files = this.existingFiles

      return files.length ? files.map(obj => obj.size).reduce((a, b) => Number(a) + Number(b), 0) : 0
    },
    maximumFileSize () {
      return 104857600
    }
  },
  methods: {
    openFileUploader () {
      this.$refs.uploader.value = ''
      this.$refs.uploader.click()
    },
    setFiles () {
      const files = this.$refs.uploader.files
      if (!files.length) return

      this.files = files
      this.uploadFiles()
    },
    uploadFiles () {
      const files = this.files
      const filesTotalSize = this.filesTotalSize
      const maximumFileSize = 104857600
      const filesSize = Array.from(files).map(obj => obj.size).reduce((a, b) => Number(a) + Number(b), 0)

      if ((filesTotalSize + filesSize) > maximumFileSize) {
        this.$store.dispatch('defaultNotificationToastsAdd', {
          title: 'Files upload',
          message: `Files haven't been uploaded as it reached the limit of 100MB.`,
          status: 'error'
        })
        return
      }

      for (let file of files) {
        const fileName = file.name
        const fileSize = file.size

        if (fileSize > maximumFileSize) {
          this.$store.dispatch('defaultNotificationToastsAdd', {
            title: 'File upload',
            message: `File hasn't been uploaded as it's bigger than 100MB.`,
            status: 'error'
          })
          return
        } else if (filesTotalSize > maximumFileSize || (filesTotalSize + fileSize) > maximumFileSize) {
          this.$store.dispatch('defaultNotificationToastsAdd', {
            title: 'File upload',
            message: `File hasn't been uploaded as you reached 100MB limit of files per course.`,
            status: 'error'
          })
          return
        }

        this.$store.dispatch(this.generatePresignedUrlDispatchName, { name: fileName, size: fileSize }).then(response => {
          const formInputs = response.data.formInputs
          const formAttributes = response.data.formAttributes
          const key = JSON.parse(JSON.stringify(formInputs['key']))
          const url = formAttributes.action + '/' + key
          let form = new FormData()

          form.append('acl', formInputs['acl'])
          form.append('key', formInputs['key'])
          form.append('X-Amz-Credential', formInputs['X-Amz-Credential'])
          form.append('X-Amz-Algorithm', formInputs['X-Amz-Algorithm'])
          form.append('X-Amz-Date', formInputs['X-Amz-Date'])
          form.append('Policy', formInputs['Policy'])
          form.append('X-Amz-Signature', formInputs['X-Amz-Signature'])
          form.append('file', file, fileName)

          this.$store.dispatch(this.setFileDispatchName, { name: fileName, size: fileSize, url: url, loaded: 0, total: 0, new: true })
          this.$store.dispatch(this.uploadFileDispatchName, {
            action: formAttributes.action,
            enctype: formAttributes.enctype,
            method: formAttributes.method,
            fileUrl: url,
            form
          })
        })
      }
    },
    removeFile (data) {
      this.$store.dispatch(this.removeFileDispatchName, data)
    },
    setFileProp (data) {
      this.$store.dispatch(this.setFilePropDispatchName, data)
    },
    handleSetPreHiddenFiles () {
      const preHiddenFiles = this.preHiddenFiles

      if (preHiddenFiles && preHiddenFiles.length) {
        for (let hiddenFile of preHiddenFiles) {
          this.setFileProp({ url: hiddenFile, key: 'hide', value: true })
        }
      }
    }
  },
  mounted () {
    this.handleSetPreHiddenFiles()
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/default/shared/componentsystemuploaderpdf";
</style>
